import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, ReactElement, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { ProjectStatus } from "../../../../../../../../api/services/project/model";
import {
  Column,
  FormController,
  MuiBox,
  MuiDateField,
  MuiSelect,
  MuiTable,
  MuiTextField,
  RowProps,
  RowValue,
} from "../../../../../../../../components";
import { StatusBadge } from "../../../../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import { MAX_LENGTH_50 } from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  filterListByFilterKeyValue,
  isEmpty,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import {
  ProjectStatusListRow,
  createProjectStatus,
  projectStatusResultKey,
} from "../../../../../../modelsAndSchemas/Models";
import { projectStatusSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ProjectStatusResultWrapper } from "./ProjectStatusResult.style";

interface ProjectStatusResultProp {
  rows: ProjectStatus[];
  pageId: string;
}

export const ProjectStatusResult: FC<ProjectStatusResultProp> = ({
  rows,
  pageId,
}) => {
  const projectStatusList = useMemo(() => {
    return isEmpty(rows) ? [createProjectStatus] : rows;
  }, [rows]);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState<
    ProjectStatusListRow[]
  >([]);
  const [results, setResult] = useState<ProjectStatusListRow[]>([]);
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    resetPagination();
    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const mapData = projectStatusList.map((status) => ({
        ...status,
        statusLabel: fetchValueByKeyOfDropdown(
          projectStatusSiteCoreKeys.projectStatus,
          projectStatusSiteCoreKeys.statusList,
          status.status,
        ),
        createdTimeStamp: convertToUserFormat(
          status.createdTimeStamp,
          userCountryDateFormat,
        ),
      }));
      setResult(mapData);
      setFilteredResults(mapData);
    }
  }, [projectStatusList, siteCoreLoading]);

  const { control, setValue, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [projectStatusResultKey.createdTimeStamp]: "",
      [projectStatusResultKey.status]: "",
      [projectStatusResultKey.comment]: "",
      [projectStatusResultKey.createdByName]: "",
    },
  });

  const tableColumnFilterWatch = useWatch({ control });

  useEffect(() => {
    if (isNotEmpty(results)) {
      const filterList = filterListByFilterKeyValue(
        results,
        tableColumnFilterWatch,
      ) as ProjectStatusListRow[];
      setFilteredResults([...filterList]);
      resetPagination();
    }
  }, [tableColumnFilterWatch]);

  const getLabel = (key: string) => {
    return fetchLabel(projectStatusSiteCoreKeys.projectStatus, key);
  };

  const columns: Column[] = [
    {
      id: projectStatusResultKey.createdTimeStamp,
      label: getLabel(projectStatusSiteCoreKeys.createdModifiedDateTableColumn),
      isSortVisible: true,
    },
    {
      id: projectStatusResultKey.status,
      label: getLabel(projectStatusSiteCoreKeys.statusTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => (
        <StatusBadge status={row.status} label={row.statusLabel} />
      ),
    },

    {
      id: projectStatusResultKey.createdByName,
      label: getLabel(projectStatusSiteCoreKeys.createdModifiedByTableColumn),
      isSortVisible: true,
    },
    {
      id: projectStatusResultKey.comment,
      label: commonLabels.comment,
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiNotesToolTip message={row.comment} />
        </MuiBox>
      ),
    },
  ];

  const tableHeaderForm = [
    <FormController
      key={projectStatusResultKey.createdTimeStamp}
      control={control}
      name={projectStatusResultKey.createdTimeStamp}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          name={projectStatusResultKey.createdTimeStamp}
          clearable
          value={
            getValues(projectStatusResultKey.createdTimeStamp) !== ""
              ? dayjs(
                  getValues(projectStatusResultKey.createdTimeStamp),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              projectStatusResultKey.createdTimeStamp,
              newValue?.format(userCountryDateFormat) || "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      key={projectStatusResultKey.status}
      control={control}
      name={projectStatusResultKey.status}
    >
      <MuiSelect
        label=""
        placeholder={commonLabels.selectPlaceHolder}
        options={fetchDropdownValues(
          projectStatusSiteCoreKeys.projectStatus,
          projectStatusSiteCoreKeys.statusList,
        )}
        visibleDefaultSelectOption
        value=""
        id={`${pageId}-${projectStatusResultKey.status}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      key={projectStatusResultKey.createdByName}
      control={control}
      name={projectStatusResultKey.createdByName}
    >
      <MuiTextField
        data-testid="filter-created-by"
        id={`${pageId}-${projectStatusResultKey.createdByName}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="comment"
      control={control}
      name={projectStatusResultKey.comment}
    >
      <MuiTextField
        data-testid="filter-comment"
        id={`${pageId}-${projectStatusResultKey.comment}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  return (
    <ProjectStatusResultWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm as ReactElement[]}
        columns={columns}
        tableResults={[
          ...(filteredResults as unknown as Record<string, RowValue>[]),
        ]}
        noDataInApi={isEmpty(results)}
        priorityResults={[]}
        noResultMessage={commonLabels.searchCriteria}
        isShowDashIfValueEmpty
      />
    </ProjectStatusResultWrapper>
  );
};
